import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { fmt } from '../data/metrics';
import { compareDeltaLabel } from '../lib/perfSettings';
import { sourceMeta } from '../data/reservationSources';
import type {
  AgentFocus,
  ConnectedSourceId,
  GuestReviewOutletRow,
  GuestReviewsPayload,
  HeroNugget,
  NorthStarPayload,
  OpsCommandPerformancePayload,
  Outlet,
  OutletPerformanceRow,
  PerformanceMetric,
  ReservationStatusGroup,
} from '../types';
import { NorthStarHero } from './NorthStarHero';
import {
  MetricInsightTile,
  MetricInsightTileSkeleton,
  OutletHeroSignalCard,
  type HeroSignal,
} from './PerformanceHeroCards';
import { EventsSection } from './EventsSection';
import { ScoreCard } from './ScoreCard';
import { SituationBrief, situationCompareLabel } from './SituationBrief';
import { PerformanceLoader } from './PerformanceLoader';
import { INSIGHTS_AGENT_NAME } from '../lib/agentBranding';
import {
  buildPerformanceOutletTableColumns,
  canShowOutletTrendHighlights,
  OUTLET_SCORE_GROUPS as SCORE_GROUPS,
  OUTLET_TREND_MIN_COVERS,
  OutletMetricCell,
  OutletReviewCell,
  OutletTableHeader,
  OutletTableSectionRow,
  OutletTableToggle,
  outletTrendScore,
  pickOutletTrendHighlights,
  sortOutletRows,
  type OutletTableSort,
} from './outletPerformanceTable';
import { Icon } from './shared/Icon';
import { highlightMetricDelta, formatSignedDelta, relDelta, trendStats } from './shared/Trend';

function signedPct(v: number | null | undefined): string {
  if (v == null) return i18n.t('performance:flat');
  return `${v >= 0 ? '+' : ''}${v}%`;
}

function absPct(v: number | null | undefined): string {
  if (v == null) return i18n.t('performance:flat');
  return `${Math.abs(v)}%`;
}

function metricValue(metric: PerformanceMetric | undefined): number {
  return metric?.value ?? 0;
}

const MIN_BUCKET_COVERS = 20;
const MIN_REL_DELTA = 8;
const STRUCTURAL_PRIORITY = 45;
/** Compact rail: keep the exception list short so the hero stays one viewport. */
const HERO_CARD_CAP = 2;
const METRIC_TILE_CAP = 6;
/** One pattern band sits beside one outlet group (Needs attention / Leading). */
const METRIC_BAND_SIZE = 3;

type OutletActivity = 'active' | 'opened' | 'closed';

type OutletMomentumScore = {
  relDelta: number;
  coversDelta: number;
  latest: number;
  baseline: number;
};

/** Detect structural open/close from leading/trailing zeros in the spark. */
function classifyOutletActivity(spark: number[] | undefined): OutletActivity {
  const s = spark ?? [];
  if (s.length < 3) return 'active';

  let firstNonZero = -1;
  let lastNonZero = -1;
  s.forEach((v, i) => {
    if (v > 0) {
      if (firstNonZero < 0) firstNonZero = i;
      lastNonZero = i;
    }
  });
  if (firstNonZero < 0) return 'active';

  const n = s.length;
  if (lastNonZero <= n - 2 && s[n - 1] === 0) return 'closed';
  if (firstNonZero >= 1) return 'opened';
  return 'active';
}

function medianNonZero(values: number[]): number {
  const nonZero = values.filter(v => v > 0).sort((a, b) => a - b);
  if (!nonZero.length) return 0;
  const mid = Math.floor(nonZero.length / 2);
  return nonZero.length % 2 === 0
    ? (nonZero[mid - 1] + nonZero[mid]) / 2
    : nonZero[mid];
}

/** Volume-aware spark WoW for active outlets; null when baseline is too thin. */
export function outletMomentumScore(
  row: OutletPerformanceRow,
): OutletMomentumScore | null {
  if ((row.spark ?? []).length < 2) return null;
  if (classifyOutletActivity(row.spark) !== 'active') return null;

  const st = trendStats(row.spark);
  if (!st.hasSeries) return null;

  const latest = st.latest;
  const s = row.spark ?? [];
  const n = s.length;
  const prevBuckets = n >= 3 ? [s[n - 2], s[n - 3]] : [s[n - 2]];
  const baseline =
    prevBuckets.reduce((a, v) => a + v, 0) / prevBuckets.length;

  const rel = relDelta(latest, baseline);
  if (rel == null) return null;

  const median = medianNonZero(s);
  const floor = Math.max(MIN_BUCKET_COVERS, Math.round(median * 0.25));
  if (latest < floor || baseline < floor) return null;
  if (Math.abs(rel) < MIN_REL_DELTA) return null;

  return { relDelta: rel, coversDelta: latest - baseline, latest, baseline };
}

/** Period vs compare-window covers momentum (same numbers as the outlet table). */
export function outletPeriodMomentumScore(
  row: OutletPerformanceRow,
): OutletMomentumScore | null {
  if (classifyOutletActivity(row.spark) === 'closed') return null;
  const covers = row.metrics.find(m => m.key === 'covers');
  const latest = covers?.value ?? row.periodCovers;
  const baseline = covers?.prior;
  if (
    baseline == null ||
    !Number.isFinite(latest) ||
    !Number.isFinite(baseline) ||
    baseline <= 0
  ) {
    return null;
  }
  const rel = relDelta(latest, baseline);
  if (rel == null) return null;
  return { relDelta: rel, coversDelta: latest - baseline, latest, baseline };
}

/** Outlet-only hero cards: structural paused/new (capacity-shaped). No volume ranking. */
export function buildOutletHeroSignals(
  outletRows: OutletPerformanceRow[],
  fleetCancelRate: number,
  fleetNoshowRate: number,
  fleetFall: number,
  aggregationLabel: string,
  prevLabel: string,
  latestLabel: string,
  comparable: boolean,
  compareWindowLabel?: string | null,
): { outletLowlights: HeroSignal[]; outletHighlights: HeroSignal[] } {
  void fleetCancelRate;
  void fleetNoshowRate;
  void fleetFall;
  void prevLabel;
  void compareWindowLabel;

  const lowCandidates: HeroSignal[] = [];
  const highCandidates: HeroSignal[] = [];

  if (comparable) {
    const latestBucketLabel =
      latestLabel || i18n.t('performance:signals.latestUnit', { unit: aggregationLabel });

    for (const o of outletRows) {
      const activity = classifyOutletActivity(o.spark);
      if (activity === 'closed') {
        lowCandidates.push({
          tone: 'watch',
          icon: 'activity',
          label: i18n.t('performance:signals.paused', { name: o.outlet.name }),
          value: i18n.t('performance:signals.zeroGuests'),
          detail: i18n.t('performance:signals.pausedDetail', { bucket: latestBucketLabel }),
          action: i18n.t('performance:signals.confirmClosed', { name: o.outlet.name }),
          priority: STRUCTURAL_PRIORITY,
          outletId: o.outlet.id,
          structural: true,
        });
      } else if (activity === 'opened') {
        highCandidates.push({
          tone: 'watch',
          icon: 'pin',
          label: i18n.t('performance:signals.newOutlet', { name: o.outlet.name }),
          value: fmt.num(o.periodCovers),
          detail: i18n.t('performance:signals.startedMid', { covers: fmt.num(o.periodCovers) }),
          action: i18n.t('performance:signals.watchBeforeCompare', { unit: aggregationLabel }),
          priority: STRUCTURAL_PRIORITY,
          outletId: o.outlet.id,
          structural: true,
        });
      }
    }
  }
  const pickUnique = (candidates: HeroSignal[], cap: number): HeroSignal[] => {
    const picked: HeroSignal[] = [];
    const seen = new Set<string>();
    const sorted = [...candidates].sort((a, b) => b.priority - a.priority);
    for (const c of sorted) {
      const id = c.outletId ?? c.label;
      if (seen.has(id)) continue;
      seen.add(id);
      picked.push(c);
      if (picked.length >= cap) break;
    }
    return picked;
  };

  return {
    outletLowlights: pickUnique(lowCandidates, HERO_CARD_CAP),
    outletHighlights: pickUnique(highCandidates, HERO_CARD_CAP),
  };
}

export function PerformanceView({
  data,
  connected,
  onSelectOutlet,
  onSelectPerfOutlet,
  onDrill,
  loading,
  onCancelLoading,
  scopeLabel,
  activeStatus,
  guestReviews,
  guestReviewsLoading,
  heroNuggets: _heroNuggets,
  heroNuggetsLoading,
  onConnect,
  northStar,
  onNorthStarDrill,
  onNorthStarEdit,
  situationPack,
  situationLoading,
  situationProfile,
  onSituationAct,
  onSituationAsk,
  restaurantId,
  activeSource,
  onOpenLocalSearch,
  events,
  eventsLoading,
  onOpenEvent,
}: {
  data: OpsCommandPerformancePayload | undefined;
  connected: ConnectedSourceId[];
  onSelectOutlet: (o: Outlet) => void;
  /** `metricKey`: the column the outlet table is sorted by, if any. */
  onSelectPerfOutlet?: (row: OutletPerformanceRow, metricKey?: string) => void;
  onDrill?: (metricKey: string, outletId?: string) => void;
  loading?: boolean;
  /** Shown on the loader: back to the screen the change replaced. */
  onCancelLoading?: () => void;
  scopeLabel?: string;
  activeStatus?: ReservationStatusGroup[];
  activeSource?: import('../types').ReservationSourceId[] | null;
  guestReviews?: GuestReviewsPayload;
  guestReviewsLoading?: boolean;
  heroNuggets?: HeroNugget[];
  heroNuggetsLoading?: boolean;
  onConnect?: (needs: ConnectedSourceId) => void;
  northStar?: NorthStarPayload | null;
  onNorthStarDrill?: () => void;
  onNorthStarEdit?: () => void;
  situationPack?: import('../lib/situationTypes').SituationPackPayload;
  situationLoading?: boolean;
  situationProfile?: import('../lib/userSituationProfile').UserSituationProfile | null;
  onSituationAct?: (
    evidence: import('../lib/situationTypes').SituationEvidence,
    card: import('../lib/situationTypes').SituationCard,
  ) => void;
  onSituationAsk?: (prompt: string, focus?: AgentFocus) => void;
  restaurantId?: string;
  /** Growth's Local and search holds the Demandgen cards now (concept decision 5). */
  onOpenLocalSearch?: () => void;
  /** The scope's events for the period (EventsSection); a row opens its drill. */
  events?: import('../types').EventsOverview;
  eventsLoading?: boolean;
  onOpenEvent?: (name: string) => void;
}) {
  const { t } = useTranslation('performance');
  const [sort, setSort] = useState<OutletTableSort>({
    key: 'covers',
    dir: 'desc',
  });
  const [showFullOutletTable, setShowFullOutletTable] = useState(false);

  const posEnabled = connected.includes('pos');
  const reviewsEnabled =
    connected.includes('reviews') &&
    (guestReviewsLoading || !!guestReviews?.enabled);

  const reviewByOutletId = useMemo(() => {
    const map = new Map<string, GuestReviewOutletRow>();
    for (const r of guestReviews?.outletRows ?? []) {
      map.set(r.restaurantId, r);
    }
    return map;
  }, [guestReviews?.outletRows]);

  const tableColumns = useMemo(
    () =>
      data ? buildPerformanceOutletTableColumns(data, posEnabled, reviewsEnabled) : [],
    [data, posEnabled, reviewsEnabled],
  );

  const trendMetricKeys = useMemo(
    () => tableColumns.filter(c => c.kind !== 'outlet').map(c => c.key),
    [tableColumns],
  );

  const sortedOutletRows = useMemo(() => {
    if (!data) return [];
    return sortOutletRows(
      data.outletRows,
      sort,
      !data.comparable,
      reviewByOutletId,
    );
  }, [data, sort, reviewByOutletId]);

  const outletTrendHighlights = useMemo(() => {
    if (!data) return { top: [], bottom: [], eligibleCount: 0 };
    const comparable = data.comparable ?? false;
    const qualifying = data.outletRows.filter(
      r => r.periodCovers >= OUTLET_TREND_MIN_COVERS,
    );
    return pickOutletTrendHighlights(
      qualifying,
      row =>
        outletTrendScore(
          row,
          comparable,
          trendMetricKeys,
          reviewByOutletId.get(row.outlet.id),
        )?.score ?? null,
      row => row.outlet.id,
    );
  }, [data, trendMetricKeys, reviewByOutletId]);

  if (loading || !data) {
    return <PerformanceLoader onCancel={onCancelLoading} />;
  }

  const comparable = data.comparable ?? false;
  const comparePeriodLabel = data.comparePeriod?.label;
  const highlightsAvailable = canShowOutletTrendHighlights(
    outletTrendHighlights.eligibleCount,
    comparable,
  );
  const showOutletHighlights = highlightsAvailable && !showFullOutletTable;

  const renderOutletRow = (row: OutletPerformanceRow) => {
    const metricByKey = Object.fromEntries(
      (row.metrics ?? []).map(m => [m.key, m]),
    );
    const review = reviewByOutletId.get(row.outlet.id);
    return (
      <tr
        key={row.outlet.id}
        className="opt-metric-row sm-row"
        onClick={() =>
          onSelectPerfOutlet
            ? onSelectPerfOutlet(row, sort.key)
            : onSelectOutlet(row.outlet)
        }
        role="button"
      >
        {tableColumns.map(col => {
          if (col.kind === 'outlet') {
            return (
              <td key={col.key} className="opt-metric-out sm-out">
                <div className="sm-out-main">
                  <b>{row.outlet.name}</b>
                  <span>
                    {row.outlet.region} · {row.outlet.city}
                  </span>
                </div>
              </td>
            );
          }
          if (col.kind === 'reviewRating' || col.kind === 'reviewCount') {
            return (
              <OutletReviewCell
                key={col.key}
                review={review}
                kind={col.kind}
                comparable={comparable}
              />
            );
          }
          return (
            <OutletMetricCell
              key={col.key}
              metric={metricByKey[col.key]}
              comparable={comparable}
            />
          );
        })}
      </tr>
    );
  };



  const series = data.series ?? [];
  const bucketLabels = series.map(b => b.label);
  const coversMetric = data.metrics.find(m => m.key === 'covers');
  const latestLabel = bucketLabels.length
    ? bucketLabels[bucketLabels.length - 1]
    : '';
  const sparkPrevLabel =
    bucketLabels.length > 1
      ? bucketLabels[bucketLabels.length - 2]
      : t('signals.priorUnit', { unit: data.aggregationLabel });
  const fleetPrevLabel = comparable
    ? (data.comparePeriod?.label ?? sparkPrevLabel)
    : sparkPrevLabel;
  const metricByKey = new Map(data.metrics.map(m => [m.key, m]));
  const coversTrendSeries = (coversMetric?.series ?? []).length
    ? (coversMetric?.series ?? [])
    : series.map(b => b.covers);
  const coversStats = trendStats(coversTrendSeries);
  const noshowMetric = metricByKey.get('noshow');
  const cancelMetric = metricByKey.get('cancel');
  const rejectedMetric = metricByKey.get('rejected');
  const reservationsMetric = metricByKey.get('reservations');
  const coversHighlight = highlightMetricDelta(coversMetric, comparable);
  const noshowHighlight = highlightMetricDelta(noshowMetric, comparable);
  const cancelHighlight = highlightMetricDelta(cancelMetric, comparable);
  const rejectedHighlight = highlightMetricDelta(rejectedMetric, comparable);
  const coversDelta = coversHighlight?.value ?? null;
  const coversLatest = coversHighlight?.headline ?? 0;
  const noshowDelta = noshowHighlight?.value ?? null;
  const cancelDelta = cancelHighlight?.value ?? null;
  const rejectedDelta = rejectedHighlight?.value ?? null;

  const regionMomentum = data.regions.map(region => {
    const st = trendStats(region.series);
    return {
      region,
      delta: st.delta ?? region.vsPriorPct,
      latest: st.hasSeries ? st.latest : region.value,
    };
  });
  const regionWinner = [...regionMomentum]
    .filter(r => r.delta > 0)
    .sort((a, b) => b.delta - a.delta)[0];
  const regionRisk = [...regionMomentum]
    .filter(r => r.delta < 0)
    .sort((a, b) => a.delta - b.delta)[0];

  const sourceMomentum = (data.sourceBreakdown?.sources ?? []).map(source => {
    const st = trendStats(source.series);
    return {
      source,
      meta: sourceMeta(source.source),
      delta: st.delta ?? source.deltaPct,
      latest: st.hasSeries ? st.latest : source.covers,
    };
  });
  const sourceWinner = [...sourceMomentum]
    .filter(s => (s.delta ?? 0) > 0)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))[0];
  const sourceRisk = [...sourceMomentum]
    .filter(s => (s.delta ?? 0) < 0)
    .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0))[0];

  const riskSignals: HeroSignal[] = [];
  const winSignals: HeroSignal[] = [];

  if (coversDelta != null && coversDelta < 0) {
    riskSignals.push({
      tone: 'risk',
      icon: 'flag',
      label: t('signals.guestsDown'),
      value: t('signals.pctDown', { pct: absPct(coversDelta) }),
      detail: t('signals.guestsDownDetail', {
        count: fmt.num(coversLatest),
        label: latestLabel || data.aggregationLabel,
        prev: fleetPrevLabel,
      }),
      action: t('signals.openGuestsSource'),
      priority: 100 + Math.abs(coversDelta),
    });
  } else if (coversDelta != null && coversDelta >= 0) {
    winSignals.push({
      tone: 'win',
      icon: 'trendUp',
      label: t('signals.guestsUp'),
      value: signedPct(coversDelta),
      detail: t('signals.guestsUpDetail', {
        count: fmt.num(coversLatest),
        label: latestLabel || data.aggregationLabel,
      }),
      action: t('signals.openGuestsChannel'),
      priority: 100 + coversDelta,
    });
  }

  if (noshowDelta != null && noshowDelta > 0) {
    riskSignals.push({
      tone: 'risk',
      icon: 'flag',
      label: t('signals.noshowsUp'),
      value: `${noshowHighlight?.headline ?? 0}%`,
      detail: t('signals.deltaVs', {
        delta: formatSignedDelta(noshowDelta, noshowHighlight?.suffix ?? 'pt'),
        prev: fleetPrevLabel,
      }),
      action: t('signals.openNoshows'),
      priority: 88 + noshowDelta,
    });
  } else if (noshowDelta != null && noshowDelta < 0) {
    winSignals.push({
      tone: 'win',
      icon: 'check',
      label: t('signals.noshowsDown'),
      value: `${noshowHighlight?.headline ?? 0}%`,
      detail: t('signals.lowerVs', {
        pct: `${Math.abs(noshowDelta)}${noshowHighlight?.suffix ?? 'pt'}`,
        prev: fleetPrevLabel,
      }),
      action: t('signals.findImprovedSource'),
      priority: 84 + Math.abs(noshowDelta),
    });
  }

  if (cancelDelta != null && cancelDelta > 0) {
    riskSignals.push({
      tone: 'risk',
      icon: 'x',
      label: t('signals.cancelsUp'),
      value: `${cancelHighlight?.headline ?? 0}%`,
      detail: t('signals.deltaVs', {
        delta: formatSignedDelta(cancelDelta, cancelHighlight?.suffix ?? 'pt'),
        prev: fleetPrevLabel,
      }),
      action: t('signals.openCancels'),
      priority: 82 + cancelDelta,
    });
  } else if (cancelDelta != null && cancelDelta < 0) {
    winSignals.push({
      tone: 'win',
      icon: 'check',
      label: t('signals.cancelsDown'),
      value: `${cancelHighlight?.headline ?? 0}%`,
      detail: t('signals.lowerVs', {
        pct: `${Math.abs(cancelDelta)}${cancelHighlight?.suffix ?? 'pt'}`,
        prev: fleetPrevLabel,
      }),
      action: t('signals.findImprovedOutlet'),
      priority: 78 + Math.abs(cancelDelta),
    });
  }

  if (rejectedDelta != null && rejectedDelta > 0 && (rejectedHighlight?.headline ?? 0) > 0) {
    riskSignals.push({
      tone: 'risk',
      icon: 'flag',
      label: t('signals.rejectedUp'),
      value: fmt.num(rejectedHighlight?.headline ?? 0),
      detail: t('signals.deltaVs', {
        delta: formatSignedDelta(rejectedDelta, rejectedHighlight?.suffix ?? '%'),
        prev: fleetPrevLabel,
      }),
      action: t('signals.openRejected'),
      priority: 74 + rejectedDelta,
    });
  }

  if (sourceRisk && (sourceRisk.delta ?? 0) < -5) {
    riskSignals.push({
      tone: 'risk',
      icon: 'table',
      label: t('signals.sourceDelta', {
        source: sourceRisk.meta.short,
        delta: signedPct(sourceRisk.delta),
      }),
      value: signedPct(sourceRisk.delta),
      detail: t('signals.sourceDetail', {
        label: sourceRisk.meta.label,
        count: fmt.num(sourceRisk.latest),
      }),
      action: t('signals.openGuestsSourceLabel', { label: sourceRisk.meta.label }),
      priority: 64 + Math.abs(sourceRisk.delta ?? 0),
    });
  }
  if (sourceWinner && (sourceWinner.delta ?? 0) > 5) {
    winSignals.push({
      tone: 'win',
      icon: 'table',
      label: t('signals.sourceDelta', {
        source: sourceWinner.meta.short,
        delta: signedPct(sourceWinner.delta),
      }),
      value: signedPct(sourceWinner.delta),
      detail: t('signals.sourceDetail', {
        label: sourceWinner.meta.label,
        count: fmt.num(sourceWinner.latest),
      }),
      action: t('signals.openGuestsSourceLabel', { label: sourceWinner.meta.label }),
      priority: 68 + (sourceWinner.delta ?? 0),
    });
  }

  if (regionRisk && regionRisk.delta < -5) {
    riskSignals.push({
      tone: 'risk',
      icon: 'pin',
      label: t('signals.regionGuestsDown', { region: regionRisk.region.region }),
      value: signedPct(regionRisk.delta),
      detail: t('signals.regionDetail', {
        count: fmt.num(regionRisk.latest),
        share: regionRisk.region.share,
      }),
      action: t('signals.openRegion', { region: regionRisk.region.region }),
      priority: 58 + Math.abs(regionRisk.delta),
    });
  }
  if (regionWinner && regionWinner.delta > 5) {
    winSignals.push({
      tone: 'win',
      icon: 'pin',
      label: t('signals.regionGuestsUp', { region: regionWinner.region.region }),
      value: signedPct(regionWinner.delta),
      detail: t('signals.regionDetail', {
        count: fmt.num(regionWinner.latest),
        share: regionWinner.region.share,
      }),
      action: t('signals.compareRegion', { region: regionWinner.region.region }),
      priority: 62 + regionWinner.delta,
    });
  }

  // Segment nuggets feed the metric tile row - not the outlet highlights board.
  // They arrive asynchronously so the scorecard can paint first; while loading
  // we keep the strip as skeletons instead of flashing lower-priority sync tiles.
  // Momentum scales priority by raw delta %, which can explode on a tiny prior
  // base, so clamp momentum below the insight band first.
  const INSIGHT_PRIORITY_BASE = 1000;
  for (const s of riskSignals)
    s.priority = Math.min(s.priority, INSIGHT_PRIORITY_BASE - 1);
  for (const s of winSignals)
    s.priority = Math.min(s.priority, INSIGHT_PRIORITY_BASE - 1);

  const fleetCancelRate =
    typeof cancelMetric?.value === 'number' ? cancelMetric.value : 0;
  const fleetNoshowRate =
    typeof noshowMetric?.value === 'number' ? noshowMetric.value : 0;
  const fleetFall = fleetCancelRate + fleetNoshowRate;

  const { outletLowlights, outletHighlights } = buildOutletHeroSignals(
    data.outletRows,
    fleetCancelRate,
    fleetNoshowRate,
    fleetFall,
    data.aggregationLabel,
    sparkPrevLabel,
    latestLabel,
    comparable,
    data.comparePeriod?.label ?? null,
  );

  const shownOutletLowlights = outletLowlights.slice(0, HERO_CARD_CAP);
  const shownOutletHighlights = outletHighlights.slice(0, HERO_CARD_CAP);

  const metricTiles = [...riskSignals, ...winSignals]
    .sort((a, b) => b.priority - a.priority)
    .slice(0, METRIC_TILE_CAP);

  const scopeName = scopeLabel ?? t('hero.thisSelection');
  const isMultiOutlet = data.outletRows.length >= 2;
  const hasOutletSignals =
    shownOutletLowlights.length > 0 || shownOutletHighlights.length > 0;
  /** Fleet comparison board only makes sense across 2+ outlets. */
  const showOutletBoard = comparable && isMultiOutlet;
  /** Mirror Needs attention + Leading: two pattern bands share the board rhythm. */
  const splitPatternBands =
    showOutletBoard &&
    shownOutletLowlights.length > 0 &&
    shownOutletHighlights.length > 0;
  const patternBandTop = metricTiles.slice(0, METRIC_BAND_SIZE);
  const patternBandBot = metricTiles.slice(
    METRIC_BAND_SIZE,
    METRIC_TILE_CAP,
  );

  const selectHeroOutlet = (outletId: string) => {
    const row = data.outletRows.find(r => r.outlet.id === outletId);
    if (!row) return;
    if (onSelectPerfOutlet) onSelectPerfOutlet(row);
    else onSelectOutlet(row.outlet);
  };

  const useAgentPatterns =
    Boolean(onSituationAct) &&
    (Boolean(situationPack) || Boolean(situationLoading));
  // The window every highlight is measured against, said once in the header.
  const scorecardCompareLabel = comparable ? (data.comparePeriod?.label ?? null) : null;
  // The brief compares with the scorecard's window: name it from the
  // scorecard's dates, in the person's language, not the pack's own label.
  const packCompareLabel = situationPack ? situationCompareLabel(situationPack) : null;
  const heroCompareLabel =
    useAgentPatterns && situationPack
      ? packCompareLabel
        ? (scorecardCompareLabel ?? packCompareLabel)
        : situationPack.quality?.ok === false
          ? null
          : scorecardCompareLabel
      : scorecardCompareLabel;
  const heroHasBody =
    showOutletBoard ||
    useAgentPatterns ||
    heroNuggetsLoading ||
    metricTiles.length > 0;

  return (
    <div className="perf">
      {/* The statuses counted are stated in the context bar, not repeated here. */}
      {/* Intelligence brief: agent situations live inside the existing hero. */}



      <div className="perf-hero" data-ftue="perf-hero">
        <div className="ph-head has-meta">
          <div className="ph-head-copy">
            <h2 className="ph-title">{t('hero.highlights')}</h2>
            {heroCompareLabel ? (
              <p className="ph-head-sub">
                {t('situation.comparedToLabel', { label: heroCompareLabel })}
              </p>
            ) : null}
          </div>
          {useAgentPatterns ? (
            <span className="ph-agent-badge" title={t('hero.agentSub', { name: INSIGHTS_AGENT_NAME })}>
              <Icon name="bolt" size={12} />
              {INSIGHTS_AGENT_NAME}
            </span>
          ) : null}
        </div>

        {!heroHasBody ? (
          <div className="ph-brief-state is-quiet" role="status">
            <span className="ph-brief-state-ic" aria-hidden>
              <Icon name="check" size={16} stroke={2.4} />
            </span>
            <span className="ph-brief-state-copy">
              <strong>{t('hero.quietTitle')}</strong>
              <span>{t('hero.quietBody', { scope: scopeName })}</span>
            </span>
          </div>
        ) : null}

        {heroHasBody && (
          <div
            className={`ph-body${showOutletBoard ? ' has-rail' : ' metrics-only'}`}
          >
            {showOutletBoard && (
              <aside className="ph-rail" aria-label={t('hero.outletExceptions')}>
                {hasOutletSignals ? (
                  <div className="ph-board">
                    {shownOutletLowlights.length > 0 && (
                      <div className="ph-group">
                        <div className="ph-group-lab risk">
                          <Icon name="flag" size={13} />
                          {t('hero.needsAttention')}
                          {outletLowlights.length > shownOutletLowlights.length
                            ? ` · ${outletLowlights.length}`
                            : ''}
                        </div>
                        <div className="ph-cards">
                          {shownOutletLowlights.map((signal, i) => (
                            <OutletHeroSignalCard
                              key={`${signal.label}-${i}`}
                              signal={signal}
                              onSelect={selectHeroOutlet}
                              outletRows={data.outletRows}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {shownOutletHighlights.length > 0 && (
                      <div className="ph-group">
                        <div className="ph-group-lab win">
                          <Icon name="trendUp" size={13} />
                          {t('hero.leading')}
                          {outletHighlights.length >
                          shownOutletHighlights.length
                            ? ` · ${outletHighlights.length}`
                            : ''}
                        </div>
                        <div className="ph-cards">
                          {shownOutletHighlights.map((signal, i) => (
                            <OutletHeroSignalCard
                              key={`${signal.label}-${i}`}
                              signal={signal}
                              onSelect={selectHeroOutlet}
                              outletRows={data.outletRows}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ph-board-empty" role="status">
                    <span className="ph-board-empty-ic" aria-hidden>
                      <Icon name="check" size={14} />
                    </span>
                    <div className="ph-board-empty-copy">
                      <div className="ph-board-empty-title">
                        {t('hero.noOutletStoodOut')}
                      </div>
                      <div className="ph-board-empty-detail">
                        {t('hero.volumeSimilar', { scope: scopeName })}
                      </div>
                    </div>
                  </div>
                )}
              </aside>
            )}

            <div className="ph-metrics-pane">
              {useAgentPatterns && onSituationAct ? (
                <SituationBrief
                  heroChrome
                  pack={situationPack}
                  loading={situationLoading}
                  profile={situationProfile}
                  onAct={onSituationAct}
                  onAsk={onSituationAsk}
                  restaurantId={restaurantId}
                  periodStart={data.periodStart ?? data.dateRange?.start ?? null}
                  periodEnd={data.periodEnd ?? data.dateRange?.end ?? null}
                  compareStart={data.comparePeriod?.start ?? null}
                  compareEnd={data.comparePeriod?.end ?? null}
                  source={activeSource ?? null}
                  status={activeStatus ?? null}
                />
              ) : heroNuggetsLoading || metricTiles.length > 0 ? (
                splitPatternBands ||
                (heroNuggetsLoading && showOutletBoard) ? (
                  <div className="ph-board">
                    <div className="ph-group">
                      <div className="ph-group-lab">
                        <Icon name="activity" size={13} />
                        {t('hero.patterns')}
                      </div>
                      <div
                        className={`ph-metrics ph-metrics-band${heroNuggetsLoading ? ' is-loading' : ''}`}
                        aria-busy={heroNuggetsLoading || undefined}
                        aria-live="polite"
                        aria-label={
                          heroNuggetsLoading
                            ? t('hero.loadingInsights')
                            : undefined
                        }
                      >
                        {heroNuggetsLoading
                          ? Array.from(
                              { length: METRIC_BAND_SIZE },
                              (_, i) => (
                                <MetricInsightTileSkeleton
                                  key={`skel-a-${i}`}
                                  index={i}
                                />
                              ),
                            )
                          : patternBandTop.map((tile, i) => (
                              <MetricInsightTile
                                tile={tile}
                                key={`${tile.label}-${i}`}
                              />
                            ))}
                      </div>
                    </div>
                    {(heroNuggetsLoading || patternBandBot.length > 0) && (
                      <div className="ph-group">
                        <div
                          className="ph-group-lab is-spacer"
                          aria-hidden
                        >
                          <Icon name="trendUp" size={13} />
                          {t('hero.leading')}
                        </div>
                        <div
                          className={`ph-metrics ph-metrics-band${heroNuggetsLoading ? ' is-loading' : ''}`}
                        >
                          {heroNuggetsLoading
                            ? Array.from(
                                { length: METRIC_BAND_SIZE },
                                (_, i) => (
                                  <MetricInsightTileSkeleton
                                    key={`skel-b-${i}`}
                                    index={i + METRIC_BAND_SIZE}
                                  />
                                ),
                              )
                            : patternBandBot.map((tile, i) => (
                                <MetricInsightTile
                                  tile={tile}
                                  key={`${tile.label}-b-${i}`}
                                />
                              ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ph-group">
                    <div className="ph-group-lab">
                      <Icon name="activity" size={13} />
                      {t('hero.patterns')}
                    </div>
                    <div
                      className={`ph-metrics${heroNuggetsLoading ? ' is-loading' : ''}`}
                      aria-busy={heroNuggetsLoading || undefined}
                      aria-live="polite"
                      aria-label={
                        heroNuggetsLoading
                          ? t('hero.loadingInsights')
                          : undefined
                      }
                    >
                      {heroNuggetsLoading
                        ? Array.from({ length: METRIC_TILE_CAP }, (_, i) => (
                            <MetricInsightTileSkeleton
                              key={`skel-${i}`}
                              index={i}
                            />
                          ))
                        : metricTiles.map((tile, i) => (
                            <MetricInsightTile
                              tile={tile}
                              key={`${tile.label}-${i}`}
                            />
                          ))}
                    </div>
                  </div>
                )
              ) : null}
            </div>
          </div>
        )}
      </div>




      {northStar && onNorthStarDrill && onNorthStarEdit && (
        <NorthStarHero
          payload={northStar}
          onDrill={onNorthStarDrill}
          onEdit={onNorthStarEdit}
        />
      )}

      <div data-ftue="perf-scorecard">
        <div className="sec-head">
          <h2>{t('scorecardTitle')}</h2>
          <span className="sh-meta">
            {data.periodLabel}
            {comparable && (
              <>
                {' · '}
                {compareDeltaLabel(
                  comparePeriodLabel ? { label: comparePeriodLabel } : null,
                )}
              </>
            )}
          </span>
        </div>
        {SCORE_GROUPS.map(group => {
          const metrics = group.keys
            .map(k => metricByKey.get(k))
            .filter((m): m is PerformanceMetric => !!m);
          if (!metrics.length) return null;
          return (
            <div className="scard-group" key={group.id}>
              <div className="scard-group-lab">{t(`groups.${group.id}`)}</div>
              <div className="scards">
                {metrics.map(m => (
                  <ScoreCard
                    key={m.key}
                    m={m}
                    onDrill={onDrill}
                    onConnect={onConnect}
                    comparable={comparable}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <EventsSection data={events} loading={eventsLoading} onOpenEvent={onOpenEvent} />

      <div data-ftue="perf-outlets">
        <div className="sec-head" style={{ marginTop: 22 }}>
          <h2>{t('outletPerf.title')}</h2>
          <span className="sh-meta">
            {showOutletHighlights
              ? t('outletPerf.topBottomMeta', { count: data.outletRows.length })
              : scopeLabel
                ? t('outletPerf.inScope', { count: data.outletRows.length, scope: scopeLabel })
                : t('outletPerf.everyOutlet')}{' '}
            {t('outletPerf.forPeriod', { period: data.periodLabel })}
          </span>
        </div>
        <div className="ol-card optbl-card">
          <div className="optbl-hint">
            <Icon name="info" size={14} />
            {showOutletHighlights ? (
              <>
                {t('outletPerf.hintHighlights')}
                {comparable &&
                  t('outletPerf.hintArrows', {
                    delta: compareDeltaLabel(
                      comparePeriodLabel ? { label: comparePeriodLabel } : null,
                    ),
                  })}
                {t('outletPerf.hintClickRow')}
              </>
            ) : (
              <>
                {t('outletPerf.hintFull')}
                {comparable &&
                  t('outletPerf.hintArrows', {
                    delta: compareDeltaLabel(
                      comparePeriodLabel ? { label: comparePeriodLabel } : null,
                    ),
                  })}
                {t('outletPerf.hintClickRow')}
              </>
            )}
          </div>
          <div className="optbl-scroll sm-scroll">
            <table className="opt-metric-table sm-table">
              <OutletTableHeader
                columns={tableColumns}
                sort={sort}
                onSort={setSort}
                sortDisabled={showOutletHighlights}
              />
              <tbody>
                {showOutletHighlights ? (
                  <>
                    <OutletTableSectionRow
                      label={t('table.topMomentum')}
                      colSpan={tableColumns.length}
                      tone="top"
                    />
                    {outletTrendHighlights.top.map(renderOutletRow)}
                    <OutletTableSectionRow
                      label={t('table.bottomMomentum')}
                      colSpan={tableColumns.length}
                      tone="bottom"
                    />
                    {outletTrendHighlights.bottom.map(renderOutletRow)}
                  </>
                ) : (
                  sortedOutletRows.map(renderOutletRow)
                )}
              </tbody>
            </table>
          </div>
          {highlightsAvailable && (
            <OutletTableToggle
              expanded={showFullOutletTable}
              totalCount={data.outletRows.length}
              onToggle={() => setShowFullOutletTable(v => !v)}
            />
          )}
        </div>
      </div>

      {/* Occupancy rhythm chart + blocked times live in the Occupancy drill-down.
          See DrillPanels `regimeGrid` / `blockedImpact`. */}

      {onOpenLocalSearch ? (
        <p className="perf-local-link">
          <Icon name="pin" size={14} />
          <span>{t('localSearchLink.text')}</span>
          <button type="button" className="perf-local-link-btn" onClick={onOpenLocalSearch}>
            {t('localSearchLink.action')}
            <Icon name="chevronRight" size={13} />
          </button>
        </p>
      ) : null}
    </div>
  );
}
